import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Facebook, Menu, X, Info, Star, Building2, HardHat, ShieldCheck } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import ConstructionInquiryForm from "@/components/ConstructionInquiryForm";

/*
  A.C. Tajanlangit Construction, Builders & Development Corporation — concept site.
  Corporate / premium / resort-and-institutional identity: gold-on-charcoal,
  editorial project portfolio, formal leadership + standards, corporate enquiry.
*/

const GOLD = "#B08D3E";
const GOLD_LIGHT = "#D9BB72";
const INK = "#12130F";
const CREAM = "#F7F4EC";

const FB = "https://www.facebook.com/ACTajanlangit/";
const NAME = "A.C. Tajanlangit Construction";
const FULL_NAME = "A.C. Tajanlangit Construction, Builders & Development Corporation";
const SLUG = "ac-tajanlangit-construction-concept";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

const P = "/construction/tajanlangit";

export default function TajanlangitConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const links = [["Capabilities", "#capabilities"], ["Projects", "#projects"], ["Company Scale", "#company-scale"], ["Standards", "#standards"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Manrope', sans-serif" }} data-testid="tajanlangit-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&family=Manrope:wght@300;400;500;600;700&display=swap');
        .act-serif { font-family: 'Cormorant Garamond', serif; }
      `}</style>

      <div data-testid="construction-disclaimer" className="bg-[#0B0C09] text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2 px-4">
          <Info size={13} className="shrink-0 text-white/60" />
          Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {FULL_NAME}.
        </span>
      </div>

      <header className={`sticky top-0 z-40 transition-shadow ${scrolled ? "shadow-[0_4px_24px_rgba(0,0,0,0.12)]" : ""}`} style={{ background: INK }}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="act-logo" className="flex items-center gap-3">
            <img src={`${P}/logo.jpg`} alt={NAME} className="h-11 w-11 object-contain" />
            <span className="act-serif text-lg text-white tracking-wide">A.C. TAJANLANGIT</span>
          </a>
          <div className="hidden lg:flex items-center gap-9">
            {links.map(([l, h]) => (
              <a key={h} href={h} className="text-sm font-medium text-white/70 hover:text-white transition-colors">{l}</a>
            ))}
          </div>
          <a href="#contact" data-testid="act-header-cta" className="hidden sm:inline-flex rounded-sm px-5 py-2.5 text-sm font-semibold" style={{ background: GOLD, color: INK }}>
            Request a Proposal
          </a>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="act-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1" style={{ background: INK }}>
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 text-white/85 font-medium">{l}</a>)}
            <a href="#contact" onClick={() => setOpen(false)} data-testid="act-mobile-cta" className="block mt-2 text-center rounded-sm px-5 py-3 text-sm font-semibold" style={{ background: GOLD, color: INK }}>Request a Proposal</a>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative min-h-[86vh] flex items-end" data-testid="act-hero">
        <img src={`${P}/hero-resort-aerial.jpg`} alt="Resort development built by A.C. Tajanlangit Construction" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(18,19,15,0.15) 0%, rgba(18,19,15,0.55) 60%, rgba(18,19,15,0.92) 100%)" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 pt-40 w-full">
          <motion.div {...fadeUp} className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: GOLD_LIGHT }}>Iloilo City, Philippines</span>
            <h1 className="mt-4 text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-white act-serif font-semibold">We Build Paradise.</h1>
            <p className="mt-5 text-lg text-white/85 font-light leading-relaxed max-w-xl">
              Island resorts, hotels, land developments and commercial buildings — engineered and delivered by an in-house team covering architectural, structural, mechanical, electrical, plumbing and fire-protection work.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid="act-hero-cta" className="rounded-sm px-7 py-3.5 text-sm font-semibold transition-transform hover:-translate-y-0.5" style={{ background: GOLD, color: INK }}>Request a Proposal</a>
              <a href="#projects" data-testid="act-hero-projects" className="rounded-sm border border-white/40 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors">View Our Projects</a>
            </div>
            <p className="mt-7 text-sm text-white/70 font-light" data-testid="act-trust-signal">
              <Star size={14} className="inline -mt-0.5" style={{ color: GOLD_LIGHT }} /> 5.0 Google Rating · 10 Reviews · Iloilo City
            </p>
          </motion.div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: GOLD }}>What We Do</span>
          <h2 className="mt-3 text-4xl sm:text-5xl act-serif font-semibold leading-snug">Full-Service Construction, In-House</h2>
          <p className="mt-5 text-lg leading-relaxed text-[#12130F]/75 font-light">
            From island resorts and hotels to commercial buildings, convention centers, industrial and residential projects, our team carries a design-and-build capability through ACT Architecture Group — covering architectural, structural, mechanical, electrical, plumbing and fire-protection scopes under one roof.
          </p>
        </motion.div>
        <div className="mt-14 grid sm:grid-cols-3 gap-10 border-t pt-14" style={{ borderColor: "#DCD2B8" }}>
          {[
            { icon: Building2, title: "Resorts & Hospitality", body: "Island resorts, beachfront hotels and dive centers built for demanding tropical sites." },
            { icon: HardHat, title: "Commercial & Industrial", body: "Convention centers, commercial buildings and industrial facilities delivered on schedule." },
            { icon: ShieldCheck, title: "Residential & Land Development", title2: "", body: "Residences and land developments, from single structures to multi-phase communities." },
          ].map(({ icon: Icon, title, body }) => (
            <motion.div key={title} {...fadeUp}>
              <Icon size={26} style={{ color: GOLD }} />
              <h3 className="mt-4 text-xl font-semibold act-serif">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#12130F]/70 font-light">{body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="py-20 md:py-28" style={{ background: INK }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: GOLD_LIGHT }}>Our Work</span>
            <h2 className="mt-3 text-4xl sm:text-5xl text-white act-serif font-semibold leading-snug">Selected Projects</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { img: `${P}/villa-resort.jpg`, label: "Resort accommodation, completed" },
              { img: `${P}/workspace-render.jpg`, label: "Leasable workspace building" },
            ].map((item) => (
              <motion.div key={item.img} {...fadeUp} data-testid="act-project-item" className="overflow-hidden rounded-sm" style={{ boxShadow: "0 12px 40px rgba(0,0,0,0.4)" }}>
                <img src={item.img} alt={item.label} className="w-full h-64 md:h-80 object-cover" loading="lazy" />
                <div className="bg-white/5 px-5 py-4">
                  <p className="text-sm text-white/70 font-light">{item.label}</p>
                </div>
              </motion.div>
            ))}
            <motion.div {...fadeUp} className="rounded-sm p-8 flex flex-col justify-center" style={{ background: "rgba(176,141,62,0.12)", border: `1px solid ${GOLD}55` }}>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: GOLD_LIGHT }}>Also in our project history</p>
              <ul className="mt-4 space-y-2 text-white/80 text-sm font-light leading-relaxed">
                <li>Two Seasons Boracay &amp; Two Seasons Coron</li>
                <li>Two Seasons Bayside Hotel &amp; Dive Center</li>
                <li>Bernwood Tower &middot; 15 storeys, 379 residential and commercial units</li>
                <li>Citadines Amigo Iloilo &middot; Lime Hotel El Nido</li>
                <li>SM Park Inn by Radisson Iloilo</li>
                <li>Artist Village at Lio Tourism Estate</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* COMPANY SCALE — avoids presenting historical officer titles as current */}
      <section id="company-scale" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-3xl">
          <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: GOLD }}>Company Scale</span>
          <h2 className="mt-3 text-4xl sm:text-5xl act-serif font-semibold leading-snug">From Iloilo Landmarks to a Growing Manila Presence</h2>
          <p className="mt-5 text-lg leading-relaxed text-[#12130F]/75 font-light">
            Bernwood Tower adds a 15-storey, 379-unit residential and commercial development to a portfolio spanning hospitality, mixed-use, commercial and residential work. Public company updates in 2025 also documented an expanding Manila presence with ACT Architecture Group.
          </p>
        </motion.div>
      </section>

      {/* STANDARDS */}
      <section id="standards" className="py-20 md:py-28" style={{ background: "#EDE7D6" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14 items-center">
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: GOLD }}>How We Work</span>
            <h2 className="mt-3 text-4xl act-serif font-semibold leading-snug">A Formal Standard for Every Partner We Bring In</h2>
            <p className="mt-5 text-lg leading-relaxed text-[#12130F]/75 font-light">
              Every supplier and subcontractor on our projects is screened against a formal accreditation framework — reviewed for relevant experience, workmanship quality, financial capability and company profile before they're allowed onsite.
            </p>
          </motion.div>
          <motion.div {...fadeUp} className="overflow-hidden rounded-sm">
            <img src={`${P}/villa-resort.jpg`} alt="Resort villa exterior" className="w-full h-96 object-cover" />
          </motion.div>
        </div>
      </section>

      {/* PROJECT INQUIRY */}
      <section id="contact" className="py-20 md:py-28" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14">
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: GOLD }}>Get In Touch</span>
            <h2 className="mt-3 text-4xl act-serif font-semibold leading-snug">Request a Project Proposal</h2>
            <ul className="mt-8 space-y-6">
              <li className="flex items-start gap-4">
                <MapPin size={20} style={{ color: GOLD }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide text-[#12130F]/50">Office</div><div>Unit 6-7, 6/F The Grid Complex, San Rafael, Mandurriao, Iloilo City</div></div>
              </li>
              <li className="flex items-start gap-4">
                <Phone size={20} style={{ color: GOLD }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide text-[#12130F]/50">Phone</div><a href="tel:+63333379947" className="hover:underline">(033) 337 9947</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Mail size={20} style={{ color: GOLD }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide text-[#12130F]/50">Email</div><a href="mailto:construction@actajanlangit.com" className="hover:underline">construction@actajanlangit.com</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Facebook size={20} style={{ color: GOLD }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide text-[#12130F]/50">Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="act-facebook-link" className="hover:underline">facebook.com/ACTajanlangit</a></div>
              </li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <ConstructionInquiryForm
              testidPrefix="act"
              accent={GOLD}
              accentText={INK}
              heading="Request a Project Proposal"
              subheading="Resort, commercial or residential — tell us about the project and our team will follow up."
              projectTypes={["Resort / Hospitality", "Commercial Building", "Industrial Facility", "Land Development", "Residential", "Other"]}
              phone="+63333379947"
              phoneDisplay="(033) 337 9947"
              messengerUrl={FB}
              email="construction@actajanlangit.com"
            />
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14" style={{ background: INK, color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={`${P}/logo.jpg`} alt={NAME} className="h-10 w-10 object-contain" />
              <span className="act-serif text-white text-lg">A.C. TAJANLANGIT</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed">Resorts, commercial and residential construction based in Iloilo City, delivered in-house from design through build.</p>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Capabilities</div>
            <ul className="text-sm space-y-2">
              <li>Architectural &amp; Structural</li>
              <li>Mechanical, Electrical &amp; Plumbing</li>
              <li>Fire Protection Systems</li>
              <li>Land Development</li>
            </ul>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Contact</div>
            <p className="text-sm">Unit 6-7, 6/F The Grid Complex,<br />San Rafael, Mandurriao, Iloilo City</p>
            <p className="text-sm mt-2">(033) 337 9947</p>
            <div className="flex gap-4 mt-4">
              <a href={FB} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} className="hover:text-white transition-colors" /></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs">
          <p>This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {FULL_NAME}. Project details, licenses and availability should be confirmed directly with the company.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="construction-sticky-contact"
        className="lg:hidden fixed bottom-4 inset-x-4 z-50 rounded-sm px-6 py-3.5 text-center text-sm font-semibold shadow-xl" style={{ background: GOLD, color: INK }}>
        Message {NAME}
      </a>
      <ConceptCTAPill slug={SLUG} />
    </div>
  );
}
